import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  BehaviorSubject,
  filter,
  map,
  Observable,
  of,
  shareReplay,
  tap,
} from 'rxjs';
import { JobPayer } from 'src/app/job/models/job-detail/details/job-detail-payer.model';
import { PropertyUserResult } from 'src/app/job/models/job-detail/details/job-detail-property-user-response.model';
import { Payer, Property } from 'src/app/job/models/job-post/job.model';
import { PropertyExpenditureForJobType } from 'src/app/job/models/job-post/property-expenditure-job-type.model';
import { PropertyExpenditureSummary } from 'src/app/job/models/job-post/property-expenditure-summary.model';
import { CampaignAgentDataModel } from 'src/app/property/models/campaign-agent.model';
import { Events } from 'src/app/shared/constants/events.constant';
import { PropertyOwnerResultDto } from 'src/app/shared/models/property.model';
import { SearchPropertiesByAddressResult } from 'src/app/shared/models/search-property-by-address-result.model';
import { environment } from 'src/environments/environment';
import { StrataDetailsModel } from '../../../../job/models/job-post/strata-details-model';
import { TimelineRequest } from '../../../../property/models/property-activity.model';
import { BaseService } from '../base/base.service';
import { CacheService } from '../cache.service';
import { JobResolvedTenantModel } from 'src/app/property/models/job-resolved-tenant.model';
import { EventService } from '../event.service';

@Injectable({
  providedIn: 'root',
})
export class PropertyService extends BaseService {
  private readonly urlPrefix = `${environment.agencyMgmt.urlPrefix}/property`;

  constructor(http: HttpClient, private cacheService: CacheService,
              private eventService: EventService,) {
    super(http);
  }

  public getOwners(propertyId: string) {
    return this.get<PropertyOwnerResultDto[]>(
      `${this.urlPrefix}/${propertyId}/owners`
    );
  }

  public searchPropertiesByAddress(FractionOfAddress: string) {
    return this.get<SearchPropertiesByAddressResult[]>(
      `${this.urlPrefix}/search-by-address?FractionOfAddress=${FractionOfAddress}`
    );
  }

  public getPayers(
    propertyId: string,
    version: string,
    includeAll: boolean = false,
    includeInactivePayers:boolean=false,
    forceRefresh: boolean = false
  ): Observable<Payer[]> {
    const queryParams = this.queryParams({
      includeAll,
      includeInactivePayers
    });
     const cacheKey = `${this.constructor.name}_${this.getPayers.name}`;
     const subKey = `${propertyId}${queryParams}${version}`;
    if (forceRefresh) {
      this.cacheService.clearCache(cacheKey); 
    }
    return this.cacheService
      .get(
       cacheKey,
       subKey,
        this.get<Payer[]>(
          `${this.urlPrefix}/payers/${propertyId}${queryParams}`
        ),
        []
      )
      .pipe(filter((payers) => !!payers));
  }

  public getPayer(
    propertyId: string,
    userId: string,
    version: string,
    includeAll: boolean = false,
    includeInactivePayers:boolean = false,
    payerRoleType : number = null,
    forceRefresh: boolean = false
  ): Observable<Payer> {
    return this.getPayers(propertyId, version, includeAll,includeInactivePayers,forceRefresh).pipe(
      map((payers) => {
        let filteredPayers = payers?.filter(
          (payer) => payer.id.toLocaleLowerCase() == userId?.toLocaleLowerCase()
            && (!payerRoleType || (payerRoleType && payer.type == payerRoleType))
        );
        let payerFoundInList = filteredPayers.length > 0;
        if (payerFoundInList) {
          return filteredPayers[0];
        } else {
          return payers?.filter((payer) =>
            payer.displayName?.includes(
              '- Paid via rental funds on behalf of owner'
            )
          )[0];
        }
      })
    );
  }

  getPropertyRelatedDataByPropertyUser(userEmail: string) {
    return this.get<any>(`${this.urlPrefix}/by-user/${userEmail}`);
  }

  searchProperty(searchKey: string) {
    return this.get<Property[]>(
      `${this.urlPrefix}/search?SearchKey=${searchKey}`
    );
  }

  advancedSearchProperty(searchKey: string, includeDeactivated: boolean) {
    return this.get<Property[]>(
      `${this.urlPrefix}/advanced-search?SearchKey=${searchKey}&IncludeDeactivated=${includeDeactivated}`
    );
  }

  getPropertyUsersByPropertyId(propertyId: string) {
    return this.get<any>(`${this.urlPrefix}/by-property-id/${propertyId}`);
  }

  getPayersListByPropertyId(propertyId: string) {
    return this.get<Payer[]>(`${this.urlPrefix}/payers/${propertyId}`);
  }

  getPropertyUsersDetail(
    propertyId: string,
    isIncludeIfCurrentPM: boolean = false,
    version: string
  ): Observable<PropertyUserResult[]> {
    if (!propertyId) return of();
    return this.cacheService.get(
      `${this.constructor.name}_${this.getPropertyUsersDetail.name}`,
      `${propertyId}${isIncludeIfCurrentPM}${version}`,
      this.get<PropertyUserResult[]>(
        `${this.urlPrefix}/users?PropertyId=${propertyId}&IsIncludeIfCurrentPM=${isIncludeIfCurrentPM}`
      ),
      [Events.JobRejectedEvent]
    );
  }

  getPropertyUser(payer: JobPayer): Observable<any> {
    return this.cacheService.get(
      `${this.constructor.name}_${this.getPropertyUser.name}`,
      `${payer.payerId}${payer.propertyId}`,
      this.get<PropertyUserResult>(
        `${this.urlPrefix}/user?UserId=${payer.payerId}&PropertyId=${payer.propertyId}`
      ),
      []
    );
  }

  public getByPropertyId(propertyId: string) {
    return this.cacheService.get(
      `${this.constructor.name}`,
      `${this.getByPropertyId.name}${propertyId}`,
      this.get<Property>(`${this.urlPrefix}/by-id/${propertyId}`),
      []
    );
  }

  public getByExtreanalRef(externalRef: string) {
    return this.get<Property>(
      `${this.urlPrefix}/by-external-ref/${externalRef}`
    );
  }

  getStrataDetailsOfProperty(propertyId: string) {
    return this.get<StrataDetailsModel>(
      `${this.urlPrefix}/strata-details/${propertyId}`
    );
  }

  getPropertyExpenditureSummary(propertyId: string) {
    return this.get<PropertyExpenditureSummary>(
      `${this.urlPrefix}/expenditure-summary/${propertyId}`
    );
  }
  getPropertyExpenditureByJobType(propertyId: string) {
    return this.get<PropertyExpenditureForJobType[]>(
      `${this.urlPrefix}/expenditure-byjobtype/${propertyId}`
    );
  }

  getPropertyLocation(propertyId: string) {
    return this.get<any>(`${this.urlPrefix}/location/${propertyId}`);
  }

  getPropertySummary(propertyId: string) {
    return this.cacheService.get(
      `${this.constructor.name}`,
      `${propertyId}`,
      this.get<any>(`${this.urlPrefix}/summary/${propertyId}`),
      [Events.PropertyUpdatedEvent]
    );

  }

  GetAllPropertyUsers(propertyId: string) {
    return this.get<any>(`${this.urlPrefix}/all-users/${propertyId}`);
  }

  getPropertyAttachments(propertyId: string) {
    return this.get<any>(`${this.urlPrefix}/attachments/${propertyId}`);
  }

  updateOwnerApprovalPercentage(
    propertyId: string,
    ownerApprovalPercentage: number
  ) {
    return this.put<boolean>(`${this.urlPrefix}/ownerApprovalPercentage`, {
      propertyId,
      ownerApprovalPercentage,
    });
  }

  updateOwnerThreshold(propertyId: string, ownerThreshold: number) {
    return this.put<boolean>(`${this.urlPrefix}/ownerThreshold`, {
      propertyId,
      ownerThreshold,
    });
  }

  updateRealtyAssistThreshold(propertyId: string, realtyAssistThreshold: number) {
    return this.put<boolean>(`${this.urlPrefix}/realtyAssistThreshold`, {
      propertyId,
      realtyAssistThreshold,
    });
  }

  getPropertyTimeline(timelineRequest: TimelineRequest) {
    var endurl = `${this.urlPrefix}/timeline/${timelineRequest.propertyId}/${timelineRequest.pageNo}/${timelineRequest.pageSize}/${timelineRequest.filterType}`;
    return this.get<any>(endurl);
  }

  printPropertyTimeLine(propertyId: string | null, jobNumber: string = '') {
    return this.post<any>(`${this.urlPrefix}/printTimeline`, {
      propertyId,
      jobNumber,
    });
  }

  getCampaignAgentData(propertyId: string): Observable<CampaignAgentDataModel> {
    return this.cacheService.get(
      `${this.constructor.name}_${this.getCampaignAgentData.name}`,
      propertyId,
      this.get<CampaignAgentDataModel>(
        `${this.urlPrefix}/campaignPayment/${propertyId}`
      ),
      [Events.OrganizationSettingsChangedEvent]
    );
  }

  getAssignProperyMangersByPropertyId(propertyId: string) {
    return this.get<any>(
      `${this.urlPrefix}/assigned-property-managers/${propertyId}`
    );
  }

  getProperyOwnerValidationByPropertyId(propertyId: string) {
    return this.get<any>(`${this.urlPrefix}/propertyOwnerStatus/${propertyId}`);
  }
  saveDefaultPayer(propertyId: string, applicationUserId: string) {
    return this.put<boolean>(`${this.urlPrefix}/defaultPayer`, {
      propertyId,
      applicationUserId,
    }).pipe(
      tap(() => {
        this.eventService.dispatch(Events.PropertyUpdatedEvent);
      })
    );
  }
  getResolvedJobByTenant(propertyId: string) {
    return this.get<JobResolvedTenantModel[]>(
      `${this.urlPrefix}/job/getTenantResolvedJobCounts?propertyId=${propertyId}`
    );
  }
  updateNotifyOwnerForFutureJobs(propertyId: string, notifyOwnerForFutureJobs: boolean) {
    return this.put<boolean>(`${this.urlPrefix}/notifyOwnerForFutureJobs`, {
      propertyId,
      notifyOwnerForFutureJobs
    });
  }
}
